import { ButtonGroup, IconButton, Menu, MenuItem, Typography } from '@mui/joy';
import { InputBase } from '@mui/material';
import { CaretDown, Copy } from '@phosphor-icons/react';
import { Page, Text, View, Document, pdf } from '@react-pdf/renderer';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  styles,
  Header,
  GrayLine,
} from '~/assets/utils/pdfs/common/CommonPDFComponents';
import { Button } from '~/components/ui/button';

const options = ['CSV Format', 'PDF Format'];

interface BackupCodeFieldProps {
  backupCode: string | number;
}

const BackupCodeField = ({ backupCode }: BackupCodeFieldProps) => {
  const [open, setOpen] = useState(false);

  const actionRef = useRef<() => void | null>(null);
  const anchorRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(1);

  const handleClick = () => {
    if (options[selectedIndex] == 'CSV Format') {
      generateReportKeyCSV();
    } else {
      generateBackupCodePDF();
    }
  };

  const handleMenuItemClick = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const copyTextToClipboard = (textValue: string): void => {
    navigator.clipboard.writeText(textValue);
  };

  // Create Document Component
  const BackupCodeDocument = (
    <Document>
      <Page size='A4' style={styles.page} wrap>
        <Header />
        <View style={{ padding: '0 28px' }}>
          <Text style={{ ...styles.sizeHeading, ...styles.bold }}>
            Backup Code
          </Text>
          <View style={{ marginTop: 6 }}>
            <GrayLine />
          </View>
        </View>
        <View style={styles.main}>
          <Text style={styles.sizeBody}>{backupCode}</Text>
        </View>
      </Page>
    </Document>
  );

  const generateBackupCodePDF = async () => {
    const pdfBlob = await pdf(BackupCodeDocument).toBlob();

    if (pdfBlob) {
      const fileURL = URL.createObjectURL(pdfBlob);
      const hiddenLink = document.createElement('a');
      hiddenLink.href = fileURL;
      hiddenLink.setAttribute('download', 'backup_code.pdf');
      document.body.appendChild(hiddenLink);
      hiddenLink.click();
      hiddenLink.parentNode?.removeChild(hiddenLink);
    }
  };

  const generateReportKeyCSV = async () => {
    const csvContent = `Backup Code\n${backupCode}`;
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    const fileURL = URL.createObjectURL(csvBlob);
    const hiddenLink = document.createElement('a');
    hiddenLink.href = fileURL;
    hiddenLink.setAttribute('download', 'backup_code.csv');
    document.body.appendChild(hiddenLink);
    hiddenLink.click();
    hiddenLink.parentNode?.removeChild(hiddenLink);
  };

  return (
    <div className='h-full'>
      <div className='flex flex-row items-center justify-between mt-4'>
        <Typography level='title-sm' component='span'>
          <FormattedMessage defaultMessage='Backup Code' />
        </Typography>

        <div>
          <ButtonGroup
            ref={anchorRef}
            variant='outlined'
            aria-label='split button'
            sx={{ background: '#E0F9F9', border: 'none' }}
          >
            <Button onClick={handleClick}>
              <Typography
                level='title-sm'
                sx={{ textTransform: 'none', color: '#00CECF' }}
              >
                <FormattedMessage defaultMessage='Download Backup Code' />
              </Typography>
            </Button>

            <IconButton
              aria-controls={open ? 'split-button-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label='select merge strategy'
              aria-haspopup='menu'
              onMouseDown={() => {
                setOpen(!open);
              }}
              onKeyDown={() => {
                setOpen(!open);
              }}
              onClick={() => {
                actionRef.current?.();
              }}
            >
              <CaretDown size={20} weight='fill' color=' #00CECF' />
            </IconButton>
          </ButtonGroup>

          <Menu
            open={open}
            onClose={() => setOpen(false)}
            anchorEl={anchorRef.current}
            sx={{ zIndex: 1500, width: '9rem' }}
          >
            {options.map((option, index) => (
              <MenuItem
                key={option}
                disabled={index === 2}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                {option}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>

      <div className='flex items-center bg-[#f2f2f2] border-radius-[6px] p-2 mt-2 mb-1'>
        <InputBase
          value={backupCode}
          readOnly
          fullWidth
          sx={{ marginLeft: '10px' }}
        />
        <IconButton onClick={() => copyTextToClipboard(String(backupCode))}>
          <Copy size={24} />
        </IconButton>
      </div>
    </div>
  );
};

export default BackupCodeField;
